import { HttpErrorResponse } from '@angular/common/http';
import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Reducers} from '@app/reducers';
import {ConfigService} from '@config/config.service';
import * as selectFromLogin from '@login/ngrx/login.selectors';
import {Actions, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {TaskChangeStatusRequest} from '@services/associations/model/taskChangeStatusRequest';
import {TaskRequest} from '@services/associations/model/taskRequest';
import {TaskResponse} from '@services/associations/model/taskResponse';
import {ConfirmationDialogComponent} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import {ListDialogComponent} from '@shared/components/list-dialog/list-dialog.component';
import {SelectorDialogComponent} from '@shared/components/selector-dialog/selector-dialog.component';
import {PermissionsModel} from '@shared/models/permission.model';
import {CrmService} from '@shared/services/crm.service';
import {SharedService} from '@shared/shared.service';
import {ToastrService} from 'ngx-toastr';
import {BehaviorSubject, catchError, EMPTY, Observable, Subject, takeUntil} from 'rxjs';
import {filter, map, take} from 'rxjs/operators';
import * as leadsBoardActions from '@crm/ngrx/actions/leads-board.actions';
import {ProjectDialogComponent} from "@crm/components/projects/project-dialog/project-dialog.component";
import {ProjectResponse} from "@services/associations/model/projectResponse";
import {WebOwnProfileModel} from "@services/back-office/model/webOwnProfileModel";
import StatusEnum = TaskRequest.StatusEnum;
import ClosedStatusEnum = TaskChangeStatusRequest.ClosedStatusEnum;
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
    selector: 'app-change-status-dialog',
    templateUrl: './change-status-dialog.component.html',
    styleUrls: ['./change-status-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChangeStatusDialogComponent implements OnInit, OnDestroy {
    /** Loading */
    loading$ = new BehaviorSubject(false);
    /** Form */
    statusForm: UntypedFormGroup;
    /** Current User */
    currentUser$: Observable<WebOwnProfileModel | null>;
    currentUser: WebOwnProfileModel | null;
    /** Permissions */
    permissions$: Observable<PermissionsModel[] | null>;
    permissions: PermissionsModel[] | null;
    success = true;
    closedStatus: ClosedStatusEnum;
    /** Subject to destroy Subscriptions */
    private destroyed$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            task: TaskResponse,
            status?: StatusEnum
        },
        private dialogRef: MatDialogRef<ChangeStatusDialogComponent>,
        private formBuilder: UntypedFormBuilder,
        private crmService: CrmService,
        private translateService: TranslateService,
        private toastrService: ToastrService,
        private store$: Store<Reducers>,
        private actions$: Actions,
        private sharedService: SharedService,
        private config: ConfigService
    ) {
        /** Select Current User */
        this.currentUser$ = this.store$.pipe(select(selectFromLogin.selectCurrentUser));
        /** Permissions */
        this.permissions$ = this.store$.pipe(select(selectFromLogin.selectPermissions));
    }

    ngOnInit(): void {
        if (this.data.status && this.data.status === 'FAIL') {
            this.success = false;
        }

        if (!this.statusForm) {
            this.initForm();
        }
        /** Get Permissions From Observable */
        this.getPermissionsFromObservable();
        /** Get Current User From Observable */
        this.getCurrentUserFromObservable();
        /** Watch Actions */
        this.watchChangeStatusSuccess();
        this.watchChangeStatusFail();
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.loading$.complete();
    }

    initForm() {
        this.statusForm = this.formBuilder.group({
            type: ['NOT_NEED', [Validators.required]],
            comment: ['', [Validators.required]],
        });
    }

    validateComment(event: any) {
        if (event.value === 'NOT_NEED' || event.value === 'OTHER_SOFT') {
            this.statusForm.get('comment')!.setValidators([Validators.required]);
            this.statusForm.get('comment')!.updateValueAndValidity();
        } else {
            this.statusForm.get('comment')!.clearValidators();
            this.statusForm.get('comment')!.updateValueAndValidity();
        }
    }

    /** Get Permissions from Observable */
    getPermissionsFromObservable() {
        this.permissions$.pipe(
            filter(data => !!data),
            take(1)
        ).subscribe((permissions: PermissionsModel[] | null) => {
                this.permissions = permissions;
            }
        );
    }

    /** Get Current User From Observable */
    getCurrentUserFromObservable() {
        this.currentUser$.pipe(
            filter(data => !!data),
            take(1),
        ).subscribe((currentUser: WebOwnProfileModel | null) => {
            this.currentUser = currentUser;
        });
    }

    createProject() {
        this.closedStatus = 'CREATED_NEW_PROJECT';
        const projectDialog$ = this.sharedService.openDialog(
            ProjectDialogComponent,
            {...this.config.rightSideDialog, ...this.config.canMinimizeDialog},
            {contact: this.data.task.contact}
        );

        (<ProjectDialogComponent>projectDialog$.componentInstance).savedData$.pipe().subscribe((data: ProjectResponse) => {
            if (data.id) {
                this.store$.dispatch(new leadsBoardActions.EditTasks(
                    {
                        taskId: this.data.task!.id!,
                        request:
                            {
                                ...this.data.task,
                                managerId: this.data.task.manager?.id,
                                contactId: this.data.task.contact?.id,
                                projectId: data.id
                            }
                    }));
                this.watchTaskUpdatedSuccess();
                this.watchTaskUpdateFailure();
            }
        });
    }

    openProjects() {
        this.closedStatus = 'ADDED_TO_PROJECT';
        const listDialog$ = this.sharedService.openDialog(
            ListDialogComponent,
            {...this.config.itemsListDialog, hasBackdrop: true},
            {
                itemsType: 'PROJECT',
                actions: {
                    canAddItem: false,
                    canEditItem: false,
                    canDeleteItem: false
                }
            }
        );

        listDialog$.afterClosed().pipe().subscribe(dialogData => {
            if (dialogData.result && dialogData.data && dialogData.data.id) {
                this.addContactToProject(this.data.task.contact!.id!, dialogData.data.id);
            }
        });
    }

    addContactToProject(userId: string, projectId: string) {
        const selectorDialog$ = this.sharedService.openDialog(
            SelectorDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                label: 'modules.crm.projects.projectDialog.labels.contactType',
                optionsTranslation: 'modules.crm.projects.contactTypes.',
                button: 'common.addItem',
                options: [
                    'TENANT',
                    'LEAD_GROUP_MEMBER',
                    'GOVERNMENT_HEAD',
                    'GOVERNMENT_MEMBER',
                    'ACCOUNTANT'
                ]
            }
        );

        selectorDialog$.afterClosed().pipe().subscribe(dialogData => {
            if (dialogData?.result && dialogData?.data) {
                this.crmService.addContactToProject(projectId, userId, dialogData.data, this.data.task.id!).pipe(
                    takeUntil(this.destroyed$),
                    map(() => {
                        this.store$.dispatch(new leadsBoardActions.EditTasks(
                            {
                                taskId: this.data.task!.id!,
                                request:
                                    {
                                        ...this.data.task,
                                        managerId: this.data.task.manager?.id,
                                        contactId: this.data.task.contact?.id,
                                        projectId: projectId
                                    }
                            }));
                        this.watchTaskUpdatedSuccess();
                        this.watchTaskUpdateFailure();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        SharedService.showErrorWithTitle(
                            this.translateService,
                            this.toastrService,
                            `responseErrors.${error.error.message}`
                        );
                        this.loading$.next(false);
                        return EMPTY;
                    }),
                ).subscribe();
            }
        });
    }

    /** Watch add project id to current task success */
    watchTaskUpdatedSuccess() {
        this.actions$.pipe(
            ofType(leadsBoardActions.LeadsBoardActionTypes.EditTasksSuccess),
            take(1),
        ).subscribe((action: leadsBoardActions.EditTasksSuccess) => {
            this.loading$.next(false);
            this.submitForm(action.payload.project?.id);
        });
    }

    /** Watch add project id to current task failed */
    watchTaskUpdateFailure() {
        this.actions$.pipe(
            ofType(leadsBoardActions.LeadsBoardActionTypes.EditTasksFailure),
            takeUntil(this.destroyed$),
        ).subscribe(() => {
            this.loading$.next(false);
        });
    }

    completeAsClient() {
        this.closedStatus = 'COMPLETED_AS_CLIENT';
        const dialog$ = this.sharedService.openDialog(
            ConfirmationDialogComponent,
            {...this.config.confirmationDialog, hasBackdrop: true},
            {
                title: SharedService.getTranslation(
                    this.translateService,
                    'modules.crm.leads.changeStatusDialog.completeAsClientWarning'
                ),
                cancelText: 'common.buttons.no',
                submitText: 'common.buttons.yes',
            }
        );

        dialog$.afterClosed().pipe().subscribe(dialogData => {
            if (dialogData) {
                this.submitForm();
            }
        });
    }

    /** Change Status */
    submitForm(projectId?: string) {
        this.loading$.next(true);

        const request: TaskChangeStatusRequest = {
            status: this.success ? 'DONE' : 'FAIL',
            closedStatus: this.closedStatus ? this.closedStatus : this.statusForm.get('type')?.value,
            comment: this.statusForm.get('comment')?.value ? this.statusForm.get('comment')?.value : '',
            ...projectId && {projectId}
        };

        this.store$.dispatch(new leadsBoardActions.ChangeTaskStatus({taskId: this.data.task!.id!, request}));
    }

    /** Watch Actions */
    watchChangeStatusSuccess() {
        this.actions$.pipe(
            ofType(leadsBoardActions.LeadsBoardActionTypes.ChangeTaskStatusSuccess),
            take(1),
        ).subscribe((action: leadsBoardActions.ChangeTaskStatusSuccess) => {
            this.loading$.next(false);
            this.closeDialog(true, action.payload);
        });
    }

    watchChangeStatusFail() {
        this.actions$.pipe(
            ofType(leadsBoardActions.LeadsBoardActionTypes.ChangeTaskStatusFailure),
            takeUntil(this.destroyed$),
        ).subscribe(() => {
            this.loading$.next(false);
        });
    }

    /** Close dialog */
    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
    }
}
